<script setup lang="ts">
import { Check } from 'lucide-vue-next'
import { computed } from 'vue'

import RelativeTime from '@/components/RelativeTime.vue'

const props = defineProps<{
  status: string
  createdAt?: string | null
  takenAt?: string | null
  acceptedAt?: string | null
  approvedAt?: string | null
  settledAt?: string | null
  rejectedAt?: string | null
  rejectionReason?: string | null
  rejectedOn?: string | null
}>()

const steps = [
  { key: 'new',      label: 'Submitted',    timestamp: () => props.createdAt },
  { key: 'taken',    label: 'Under Review', timestamp: () => props.takenAt },
  { key: 'accepted', label: 'Accepted',     timestamp: () => props.acceptedAt },
  { key: 'approved', label: 'Approved',     timestamp: () => props.approvedAt },
  { key: 'settled',  label: 'Settled',      timestamp: () => props.settledAt },
]

const ORDER = ['new', 'taken', 'accepted', 'approved', 'settled']

const currentIndex = computed(() => {
  if (props.status === 'rejected') return ORDER.indexOf(props.rejectedOn ?? 'new')
  return ORDER.indexOf(props.status)
})

function stepState(i: number): 'complete' | 'current' | 'upcoming' {
  if (props.status === 'rejected' && i === currentIndex.value) return 'current'
  if (i < currentIndex.value) return 'complete'
  if (i === currentIndex.value) return 'current'
  return 'upcoming'
}

function isRejectedCurrent(i: number) {
  return props.status === 'rejected' && i === currentIndex.value
}

const isTerminal = computed(() => props.status === 'rejected' || props.status === 'settled')

function showPulse(i: number) {
  return !isTerminal.value && stepState(i) === 'current'
}
</script>

<template>
  <div class="space-y-4">

    <!-- ── Mobile: vertical ─────────────────────────────────────────────── -->
    <div class="flex flex-col gap-0 md:hidden">
      <div
        v-for="(step, i) in steps"
        :key="step.key"
        class="flex items-start gap-3"
      >
        <!-- dot + vertical connector -->
        <div class="flex flex-col items-center gap-y-2">
          <div class="relative flex shrink-0 items-center justify-center">
            <!-- pulse ring for current non-rejected/settled -->
            <span
              v-if="showPulse(i)"
              class="absolute size-12 animate-ping rounded-full bg-primary opacity-20"
            />
            <div
              :class="[
                'relative flex size-8 items-center justify-center rounded-full border-2 shrink-0',
                stepState(i) === 'complete'  ? 'border-primary bg-primary text-primary-foreground' :
                isRejectedCurrent(i)         ? 'border-destructive bg-destructive text-destructive-foreground' :
                stepState(i) === 'current'   ? 'border-primary bg-primary text-primary-foreground' :
                'border-muted bg-background text-muted-foreground',
              ]"
            >
              <Check v-if="stepState(i) === 'complete'" class="size-4" />
              <span v-else class="size-2.5 rounded-full bg-current" />
            </div>
          </div>
          <div
            v-if="i < steps.length - 1"
            :class="['w-0.5 flex-1 my-1', stepState(i) === 'complete' ? 'bg-primary' : 'bg-muted']"
            style="min-height: 1.5rem"
          />
        </div>
        <!-- label -->
        <div class="pb-4 pt-1">
          <p :class="['text-sm font-medium', stepState(i) === 'upcoming' ? 'text-muted-foreground' : '']">
            {{ step.label }}
          </p>
          <p v-if="step.timestamp?.()" class="text-muted-foreground text-xs mt-0.5">
            <RelativeTime :date="step.timestamp?.() ?? ''" />
          </p>
        </div>
      </div>
    </div>

    <!-- ── Desktop: horizontal ───────────────────────────────────────────── -->
    <div class="hidden items-start gap-0 md:flex">
      <div
        v-for="(step, i) in steps"
        :key="step.key"
        class="flex flex-1 flex-col items-center"
      >
        <div class="flex w-full items-center">
          <div :class="['h-0.5 flex-1', i === 0 ? 'invisible' : stepState(i) === 'upcoming' ? 'bg-muted' : 'bg-primary']" />

          <div class="relative flex shrink-0 items-center justify-center">
            <!-- pulse ring for current non-rejected/settled -->
            <span
              v-if="showPulse(i)"
              class="absolute size-12 animate-ping rounded-full bg-primary opacity-20"
            />
            <div
              :class="[
                'relative flex size-8 items-center justify-center rounded-full border-2 shrink-0',
                stepState(i) === 'complete'  ? 'border-primary bg-primary text-primary-foreground' :
                isRejectedCurrent(i)         ? 'border-destructive bg-destructive text-destructive-foreground' :
                stepState(i) === 'current'   ? 'border-primary bg-primary text-primary-foreground' :
                'border-muted bg-background text-muted-foreground',
              ]"
            >
              <Check v-if="stepState(i) === 'complete'" class="size-4" />
              <span v-else class="size-2.5 rounded-full bg-current" />
            </div>
          </div>

          <div :class="['h-0.5 flex-1', i === steps.length - 1 ? 'invisible' : stepState(i) === 'complete' ? 'bg-primary' : 'bg-muted']" />
        </div>
        <div class="mt-4 text-center">
          <p :class="['text-xs font-medium', stepState(i) === 'upcoming' ? 'text-muted-foreground' : '']">
            {{ step.label }}
          </p>
          <p v-if="step.timestamp?.()" class="text-muted-foreground text-xs">
            <RelativeTime :date="step.timestamp?.() ?? ''" />
          </p>
        </div>
      </div>
    </div>

    <!-- Rejection notice -->
    <div v-if="status === 'rejected'" class="bg-destructive/10 border-destructive/30 rounded-lg border p-4">
      <p class="text-destructive text-sm font-medium">Application Rejected</p>
      <p v-if="rejectionReason" class="text-muted-foreground mt-1 text-sm">{{ rejectionReason }}</p>
    </div>
  </div>
</template>
